import { Box, useTheme, type Theme } from '@mui/material';
import type { DependencyEdge, Ticket } from '../api';
import {
  NODE_HEIGHT,
  NODE_WIDTH,
  type GraphLayout,
  type PositionedTicket,
} from '../dependencyLayout';

/**
 * The epic's link topology, drawn by hand.
 *
 * No graph library. One epic's share of the board is a few dozen edges, which does not
 * justify 150-500 KB of cytoscape or d3-force — and a force layout jitters differently on
 * every render, so the same graph never looks like the same graph twice. Where each ticket
 * goes is decided in `dependencyLayout`; this file only draws it.
 *
 * Only linked tickets appear. Most tickets have no links, and drawing them would bury the
 * real chains in a field of isolated dots — the page lists them instead.
 */

interface Highlighting {
  /** Refs on the longest chain, drawn in the highlight colour. */
  emphasised: Set<number>;
  /** Refs to hold back, so an emphasised subset reads as the subject. Empty = draw all. */
  faded: Set<number>;
}

interface DependencyGraphProps extends Highlighting {
  layout: GraphLayout;
  edges: DependencyEdge[];
  /** Every ticket on the board — a blocker is often in another epic. */
  tickets: Ticket[];
  /** Refs belonging to another epic, drawn with a dashed border. */
  external: Set<number>;
  onTicketClick: (ref: number) => void;
}

const MARKER = { plain: 'dep-arrow', emphasised: 'dep-arrow-strong', faded: 'dep-arrow-faint' };

interface EdgeTone {
  stroke: string;
  width: number;
  opacity: number;
  marker: string | null;
}

/** How a link is drawn: held back, on the highlighted chain, or ordinary. */
function edgeTone(edge: DependencyEdge, theme: Theme, shown: Highlighting): EdgeTone {
  if (shown.faded.has(edge.from) || shown.faded.has(edge.to)) {
    return { stroke: theme.palette.text.disabled, width: 1, opacity: 0.2, marker: MARKER.faded };
  }
  const blocking = edge.kind === 'blocks';
  if (blocking && shown.emphasised.has(edge.from) && shown.emphasised.has(edge.to)) {
    return {
      stroke: theme.palette.warning.light,
      width: 2.2,
      opacity: 1,
      marker: MARKER.emphasised,
    };
  }
  return {
    stroke: theme.palette.text.secondary,
    width: blocking ? 1.5 : 1,
    opacity: blocking ? 0.72 : 0.4,
    marker: blocking ? MARKER.plain : null,
  };
}

const Arrowhead = ({ id, fill }: { id: string; fill: string }) => (
  <marker
    id={id}
    viewBox="0 0 10 10"
    refX="9"
    refY="5"
    markerWidth="6"
    markerHeight="6"
    orient="auto-start-reverse"
  >
    <path d="M 0 0 L 10 5 L 0 10 z" fill={fill} />
  </marker>
);

/**
 * Fill and label colour together, because they are one decision.
 *
 * Done and In progress are pale fills, and the theme's primary text colour is white in
 * dark mode — so the label has to be taken from the fill rather than from the surface
 * behind it, or a finished ticket becomes white-on-pale-green and unreadable.
 */
function statusPaint(status: string | null, theme: Theme): { fill: string; label: string } {
  // Decided against. Drawn, because you want to see where a chain was abandoned, but
  // receded — it gates nothing, and anything behind it is free to start.
  //
  // The FILL recedes and the label does not: `text.disabled` on `disabledBackground`
  // composites to roughly 2.5:1 in the light theme, under the 3:1 floor, and this function
  // exists to keep a ref legible against its own fill. Focus view fades whole tickets on
  // top of this, so it has to start readable.
  if (status === 'Closed') {
    return { fill: theme.palette.action.disabledBackground, label: theme.palette.text.secondary };
  }
  if (status === 'Done') {
    return {
      fill: theme.palette.success.light,
      label: theme.palette.getContrastText(theme.palette.success.light),
    };
  }
  if (status === 'In progress') {
    return {
      fill: theme.palette.primary.light,
      label: theme.palette.getContrastText(theme.palette.primary.light),
    };
  }
  // `action.hover` is a translucent overlay on the page, so the surface's own text colour
  // is the one that reads against it.
  return { fill: theme.palette.action.hover, label: theme.palette.text.primary };
}

function Edge({
  edge,
  from,
  to,
  tone,
}: {
  edge: DependencyEdge;
  from?: PositionedTicket;
  to?: PositionedTicket;
  tone: EdgeTone;
}) {
  if (!from || !to) return null;
  const blocking = edge.kind === 'blocks';
  const startX = from.x + NODE_WIDTH;
  const startY = from.y + NODE_HEIGHT / 2;
  const endY = to.y + NODE_HEIGHT / 2;
  // Leaving and arriving horizontally is what makes a long edge readable: the eye picks up
  // which side of a ticket a line joins, so direction survives the middle of the drawing.
  const reach = Math.max(28, Math.abs(to.x - startX) * 0.42);

  return (
    <path
      d={`M${startX},${startY} C${startX + reach},${startY} ${to.x - reach},${endY} ${to.x},${endY}`}
      fill="none"
      stroke={tone.stroke}
      strokeWidth={tone.width}
      opacity={tone.opacity}
      // `relates-to` is context, not order: dashed and unarrowed, so the two kinds are
      // told apart by shape rather than only by line weight.
      strokeDasharray={blocking ? undefined : '4 3'}
      markerEnd={blocking && tone.marker ? `url(#${tone.marker})` : undefined}
    />
  );
}

function TicketBox({
  position,
  ticket,
  external,
  emphasised,
  faded,
  theme,
  onClick,
}: {
  position: PositionedTicket;
  ticket?: Ticket;
  external: boolean;
  emphasised: boolean;
  faded: boolean;
  theme: Theme;
  onClick: () => void;
}) {
  const status = ticket?.status ?? null;
  const label = `DWLF-${position.ref}`;
  const border = emphasised ? theme.palette.warning.light : theme.palette.divider;
  const paint = statusPaint(status, theme);

  return (
    <g
      transform={`translate(${position.x}, ${position.y})`}
      onClick={onClick}
      style={{ cursor: 'pointer' }}
      opacity={faded ? 0.25 : 1}
    >
      {/*
        The status is in the accessible name and the tooltip, not only in the fill.
        Done/In progress/Not started is precisely a green-blue-grey axis, and a
        colour-only encoding of it is unreadable to a large minority of people.
      */}
      <title>
        {`${label} — ${ticket?.name ?? 'unknown ticket'} (${status ?? 'unknown status'}${
          external ? `, ${ticket?.epic ?? 'another epic'}` : ''
        })`}
      </title>
      <rect
        width={NODE_WIDTH}
        height={NODE_HEIGHT}
        rx={4}
        fill={paint.fill}
        stroke={border}
        strokeWidth={emphasised ? 2 : 1}
        // A dashed border is what says "this one is not part of this epic".
        strokeDasharray={external ? '4 3' : undefined}
      />
      <text
        x={NODE_WIDTH / 2}
        y={NODE_HEIGHT / 2 + 4}
        textAnchor="middle"
        fontSize={12}
        fill={paint.label}
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {label}
      </text>
    </g>
  );
}

export function DependencyGraph({
  layout,
  edges,
  tickets,
  external,
  emphasised,
  faded,
  onTicketClick,
}: DependencyGraphProps) {
  const theme = useTheme();
  const byRef = new Map(tickets.map((ticket) => [ticket.ref, ticket]));
  const position = new Map(layout.tickets.map((ticket) => [ticket.ref, ticket]));

  const highlighting = { emphasised, faded };

  return (
    // Wide graphs scroll inside the panel rather than stretching the page.
    <Box sx={{ overflowX: 'auto', py: 1 }}>
      <svg
        width={layout.width}
        height={layout.height}
        viewBox={`0 0 ${layout.width} ${layout.height}`}
        role="img"
        aria-label={`Dependency graph: ${layout.tickets.length} linked tickets, ${edges.length} links`}
      >
        <defs>
          <Arrowhead id={MARKER.plain} fill={theme.palette.text.secondary} />
          <Arrowhead id={MARKER.emphasised} fill={theme.palette.warning.light} />
          <Arrowhead id={MARKER.faded} fill={theme.palette.text.disabled} />
        </defs>

        {edges.map((edge) => (
          <Edge
            key={`${edge.from}-${edge.kind}-${edge.to}`}
            edge={edge}
            from={position.get(edge.from)}
            to={position.get(edge.to)}
            tone={edgeTone(edge, theme, highlighting)}
          />
        ))}

        {layout.tickets.map((ticket) => (
          <TicketBox
            key={ticket.ref}
            position={ticket}
            ticket={byRef.get(ticket.ref)}
            external={external.has(ticket.ref)}
            emphasised={emphasised.has(ticket.ref)}
            faded={faded.has(ticket.ref)}
            theme={theme}
            onClick={() => onTicketClick(ticket.ref)}
          />
        ))}
      </svg>
    </Box>
  );
}
